import { useEffect, useState } from 'react';
import { PostHogProvider as PHProvider } from '@posthog/react';

/**
 * PostHog, gated on the analytical category of the cookie banner.
 *
 * Replaces the PostHog Cloud snippet that used to sit in public/index.html and
 * loaded on every page view regardless of the banner. The banner (see the
 * cookie modal in pages/LandingPage.js) offers an analytical toggle and stores
 * the decision under `cookieConsent`, so analytics must actually honour it.
 *
 * Withdrawing consent takes effect on the next page load: a script that has
 * already run cannot be meaningfully unloaded.
 */

const CONSENT_KEY = 'cookieConsent';

/** Dispatched by LandingPage.js whenever a cookie decision is saved. */
export const CONSENT_EVENT = 'cookieConsentChanged';

const options = {
  api_host: process.env.REACT_APP_POSTHOG_HOST,
  defaults: '2026-05-30',
  // Our self-hosted instance only serves the unversioned /static/<name>.js
  // path, so skip the default versioned-first lookup (it 302s to /login).
  strict_script_versioning: false,
};

const hasAnalyticsConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return false;
    // The banner writes the preferences object directly; the category is
    // named `analytical` there, not `analytics`.
    return !!JSON.parse(raw).analytical;
  } catch {
    // localStorage can throw outright (Safari private mode, blocked site data)
    // and a half-written value fails the parse. Both mean no consent.
    return false;
  }
};

export function PostHogProvider({ children }) {
  const [consented, setConsented] = useState(hasAnalyticsConsent);

  useEffect(() => {
    const update = () => setConsented(hasAnalyticsConsent());
    // The custom event covers a decision made in this tab, 'storage' one made
    // in another. Neither requires a reload.
    window.addEventListener(CONSENT_EVENT, update);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener(CONSENT_EVENT, update);
      window.removeEventListener('storage', update);
    };
  }, []);

  const apiKey = process.env.REACT_APP_POSTHOG_PROJECT_TOKEN;
  if (!consented || !apiKey) return <>{children}</>;

  return (
    <PHProvider apiKey={apiKey} options={options}>
      {children}
    </PHProvider>
  );
}

export default PostHogProvider;
